import {
  useMutation,
  useQuery,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import { Workorder } from '@certhon/models';
import {
  calculateRoutingKey,
  getRouteStatus,
  canAutoSort as canAutoSortUtil,
  canManualEdit as canManualEditUtil,
  RouteStatus,
} from '@certhon/shared';
import { useCurrentUser } from '@/features/users/users.hooks';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { dayPlanningQueries } from './dayplanning.queries';
import {
  updateDayPlanningFields,
  fetchPlangroups,
  optimizeRoute,
  updateRouteManual,
} from './dayplanning.api';
import { EditableWorkorderFieldsDto } from '../workorders.api';
import { useMemo } from 'react';
import * as cp from '@certhon/cloud-permissions';
import { RouteResponse, isRouteError } from './dayplanning.types';
import { HQ_LOCATION, QUERY_CONFIG, STORAGE_KEYS } from '../shared/constants';
import { toast } from 'sonner';

/**
 * Fetch workorders for a specific day
 */
export function useDayWorkorders(date: string) {
  const currentUser = useCurrentUser();
  return useQuery({
    ...dayPlanningQueries.day(currentUser?.recnum ?? 0, date),
    enabled: !!currentUser?.recnum,
  });
}

/**
 * Fetch routes between workorders for a single technician
 * Uses techRoutes query for better cache sharing with multi-tech view
 */
export function useRoutes(
  workorders: Workorder[] | undefined,
  techId?: number,
) {
  const currentUser = useCurrentUser();

  // Determine which tech ID to use for caching
  // Priority: explicit techId > first workorder's assigned user > current user
  const effectiveTechId =
    techId ?? workorders?.[0]?.assigned_user_id ?? currentUser?.recnum ?? 0;

  // Always use techRoutes for better cache sharing with multi-tech view
  return useQuery({
    ...dayPlanningQueries.techRoutes(effectiveTechId, workorders ?? []),
    enabled: !!workorders && workorders.length > 0 && effectiveTechId > 0,
    throwOnError: false,
  });
}

/**
 * Update day planning fields mutation
 */
export function useUpdateDayPlanningMutation(
  date: string,
  selectedTechId?: number | null,
) {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();

  return useMutation({
    mutationFn: ({
      recnum,
      fields,
    }: {
      recnum: number;
      fields: EditableWorkorderFieldsDto;
    }) => updateDayPlanningFields(recnum, fields),

    onSuccess: () => {
      // If a specific technician is selected, invalidate the all-technicians query
      // Otherwise invalidate the current user's day query
      if (selectedTechId) {
        queryClient.invalidateQueries({
          queryKey: dayPlanningQueries.allTechnicians(date).queryKey,
        });
      } else if (currentUser?.recnum) {
        queryClient.invalidateQueries({
          queryKey: dayPlanningQueries.day(currentUser.recnum, date).queryKey,
        });
      }

      // Also invalidate routes queries (they depend on route_option)
      queryClient.invalidateQueries({
        queryKey: [...dayPlanningQueries.all(), 'routes'],
      });
    },
  });
}

/**
 * Fetch all technicians' workorders for a specific day
 */
export function useAllDayWorkorders(date: string) {
  return useQuery(dayPlanningQueries.allTechnicians(date));
}

/**
 * Fetch plangroups (teams) with their member user IDs
 */
export function usePlangroups() {
  return useQuery({
    queryKey: ['plangroups'],
    queryFn: fetchPlangroups,
    staleTime: QUERY_CONFIG.plangroupsStale,
  });
}

/**
 * Get/set selected plangroup IDs (persisted in localStorage)
 * Empty array = show all technicians
 */
export function useSelectedPlangroups() {
  const [selected, setSelected] = useLocalStorage<number[]>(
    STORAGE_KEYS.selectedPlangroups,
    [],
  );
  return [selected, setSelected] as const;
}

/**
 * Return type for useMultiTechRoutes hook with progress information
 */
export interface MultiTechRoutesResult {
  /** Combined routes data from all technicians (undefined while any are still loading) */
  data: RouteResponse[] | undefined;
  /** Whether any routes are still loading */
  isLoading: boolean;
  /** First error encountered (if any) */
  error: Error | undefined;
  /** Progress information for route calculation */
  progress: {
    /** Number of technician routes that have finished loading */
    completed: number;
    /** Total number of technicians being fetched */
    total: number;
    /** Whether all routes are complete */
    isComplete: boolean;
  };
}

/**
 * Fetch routes for multiple technicians by batching per-technician route queries
 * This improves caching - when user switches to individual tech tabs,
 * routes are already cached from this multi-tech view
 */
export function useMultiTechRoutes(
  workordersByTech: Map<number, Workorder[]> | undefined,
): MultiTechRoutesResult {
  // Build query configs for each technician
  const queryConfigs = useMemo(() => {
    if (!workordersByTech || workordersByTech.size === 0) {
      return [];
    }
    return Array.from(workordersByTech.entries()).map(
      ([techId, workorders]) => ({
        ...dayPlanningQueries.techRoutes(techId, workorders),
        enabled: workorders && workorders.length > 0,
        throwOnError: false as const,
      }),
    );
  }, [workordersByTech]);

  // Fetch all technician routes in parallel using useQueries
  const results = useQueries({ queries: queryConfigs });

  // Combine results with progress information
  return useMemo(() => {
    const total = results.length;

    if (total === 0) {
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
        progress: { completed: 0, total: 0, isComplete: true },
      };
    }

    const completed = results.filter(
      (q) => !q.isLoading && !q.isFetching,
    ).length;
    const isLoading = results.some((q) => q.isLoading || q.isFetching);
    const error = results.find((q) => q.error)?.error as Error | undefined;
    const isComplete = completed === total;

    // Flatten all routes into a single array (only when all are complete)
    const data = isComplete ? results.flatMap((q) => q.data ?? []) : undefined;

    return {
      data,
      isLoading,
      error,
      progress: { completed, total, isComplete },
    };
  }, [results]);
}

/**
 * Check if current user can edit day planning
 */
export function useCanEditDayPlanning() {
  const currentUser = useCurrentUser();
  return useMemo(() => {
    if (!currentUser) return false;
    const permissions = cp.decode(currentUser.permissions);
    return permissions.WB_ADMIN || false;
  }, [currentUser]);
}

/**
 * Geocode workorder addresses (fallback for when routing fails)
 */
export function useGeocodedCoordinates(workorders: Workorder[] | undefined) {
  return useQuery({
    ...dayPlanningQueries.geocode(workorders ?? []),
    enabled: !!workorders && workorders.length > 0,
  });
}

/**
 * Information about route errors for a workorder
 */
export interface WorkorderRouteError {
  /** Error on the incoming route (route TO this workorder from previous/HQ) */
  incomingError?: string;
  /** Error on the outgoing route (only set for last workorder - route to HQ) */
  outgoingError?: string;
}

/**
 * Format address for matching with route responses
 * Must match the formatAddressForGeocoding function in dayplanning.queries.ts
 */
function formatAddressForMatching(address: string): string {
  return address.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Extract route error information per workorder from the routes array
 * Maps workorder recnum -> error info for incoming/outgoing routes
 *
 * Uses the from/to addresses in route errors to match to workorders,
 * eliminating the need for fragile index tracking.
 */
export function getWorkorderRouteErrors(
  workorders: Workorder[],
  routes: RouteResponse[] | undefined,
): Map<number, WorkorderRouteError> {
  const errorMap = new Map<number, WorkorderRouteError>();

  if (!routes || workorders.length === 0) {
    return errorMap;
  }

  // Build a map of formatted address -> workorder recnum for quick lookup
  const addressToRecnum = new Map<string, number>();
  for (const wo of workorders) {
    if (wo.location_address) {
      const formatted = formatAddressForMatching(wo.location_address);
      addressToRecnum.set(formatted, wo.recnum);
    }
  }

  // Find the last workorder with an address (for outgoing route detection)
  const lastWoWithAddress = [...workorders]
    .reverse()
    .find((wo) => wo.location_address?.trim());

  // Process all route errors
  for (const route of routes) {
    if (!isRouteError(route)) continue;

    const toFormatted = formatAddressForMatching(route.to);
    const fromFormatted = formatAddressForMatching(route.from);

    // Check if this is an incoming route error (route TO a workorder)
    const targetRecnum = addressToRecnum.get(toFormatted);
    if (targetRecnum !== undefined) {
      const existing = errorMap.get(targetRecnum) ?? {};
      const combinedError = existing.incomingError
        ? `${existing.incomingError} | ${route.error}`
        : route.error;
      errorMap.set(targetRecnum, { ...existing, incomingError: combinedError });
    }

    // Check if this is a trailing route error (last workorder -> HQ)
    const hqFormatted = formatAddressForMatching(HQ_LOCATION.address);
    if (lastWoWithAddress && toFormatted === hqFormatted) {
      const lastRecnum = lastWoWithAddress.recnum;
      const lastFormatted = formatAddressForMatching(
        lastWoWithAddress.location_address ?? '',
      );

      if (fromFormatted === lastFormatted) {
        const existing = errorMap.get(lastRecnum) ?? {};
        errorMap.set(lastRecnum, { ...existing, outgoingError: route.error });
      }
    }
  }

  return errorMap;
}

/**
 * Hook to get route errors per workorder
 */
export function useWorkorderRouteErrors(
  workorders: Workorder[] | undefined,
  routes: RouteResponse[] | undefined,
): Map<number, WorkorderRouteError> {
  return useMemo(
    () => getWorkorderRouteErrors(workorders ?? [], routes),
    [workorders, routes],
  );
}

/**
 * Hook to get route errors for multi-technician view
 * Fetches routes for each technician and computes errors across all of them
 */
export function useMultiTechRouteErrors(
  workordersByTech: Map<number, Workorder[]> | undefined,
): Map<number, WorkorderRouteError> {
  // Build query configs for each technician (same as useMultiTechRoutes)
  const queryConfigs = useMemo(() => {
    if (!workordersByTech || workordersByTech.size === 0) {
      return [];
    }
    return Array.from(workordersByTech.entries()).map(
      ([techId, workorders]) => ({
        ...dayPlanningQueries.techRoutes(techId, workorders),
        enabled: workorders && workorders.length > 0,
        throwOnError: false as const,
      }),
    );
  }, [workordersByTech]);

  // Fetch all technician routes in parallel
  const results = useQueries({ queries: queryConfigs });

  // Compute errors for each technician and merge into single map
  return useMemo(() => {
    const errorMap = new Map<number, WorkorderRouteError>();

    if (!workordersByTech || workordersByTech.size === 0) {
      return errorMap;
    }

    const techEntries = Array.from(workordersByTech.entries());

    results.forEach((result, idx) => {
      if (!result.data) return;

      const [, workorders] = techEntries[idx];
      const techErrors = getWorkorderRouteErrors(workorders, result.data);

      // Merge into overall map
      techErrors.forEach((error, recnum) => {
        errorMap.set(recnum, error);
      });
    });

    return errorMap;
  }, [workordersByTech, results]);
}

/**
 * Sort workorders by planned arrive time or sort order
 */
export function sortWorkorders(workorders: Workorder[]): Workorder[] {
  return [...workorders].sort((a, b) => {
    // Primary: planned_arrive_time (chronological)
    if (a.planned_arrive_time && b.planned_arrive_time) {
      return a.planned_arrive_time.localeCompare(b.planned_arrive_time);
    }
    if (a.planned_arrive_time) return -1;
    if (b.planned_arrive_time) return 1;

    // Secondary: sort_order
    if (a.sort_order !== null && b.sort_order !== null) {
      return a.sort_order - b.sort_order;
    }
    if (a.sort_order !== null) return -1;
    if (b.sort_order !== null) return 1;

    // Fallback: execution_date
    const aDate = a.execution_date?.getTime() ?? 0;
    const bDate = b.execution_date?.getTime() ?? 0;
    return aDate - bDate;
  });
}

/**
 * Optimistically sort workorders for immediate UI feedback
 * Uses existing sort_order if available, otherwise falls back to recnum
 */
function sortOptimistically(workorders: Workorder[]): Workorder[] {
  return [...workorders].sort((a, b) => {
    // Use sort_order if both have it
    if (a.sort_order !== null && b.sort_order !== null) {
      return a.sort_order - b.sort_order;
    }
    if (a.sort_order !== null) return -1;
    if (b.sort_order !== null) return 1;

    // Fallback to recnum (original order)
    return a.recnum - b.recnum;
  });
}

// =============================================================================
// Route Ordering Clarity Hooks
// =============================================================================

/**
 * Return type for useRoutingKeyStatus hook
 */
export interface RoutingKeyStatus {
  /** Calculated routing key from current workorder data */
  currentKey: string | null;
  /** Stored routing key from database */
  storedKey: string | null;
  /** Source of sort_order: 'auto', 'manual', or null (unsorted) */
  sortOrderSource: 'auto' | 'manual' | null;
  /** Whether the routing key has changed since last calculation */
  hasChanges: boolean;
  /** Route status determined from metadata */
  status: RouteStatus;
  /** Whether the current user can trigger auto-sort */
  canAutoSort: boolean;
  /** Whether the current user can make manual route changes */
  canManualEdit: boolean;
  /** Timestamp when route was last calculated/modified */
  routingTimestamp: Date | null;
}

/**
 * Hook to detect routing key changes and determine route status
 *
 * @param workorders - Array of workorders to analyze
 * @returns Routing key status information
 */
export function useRoutingKeyStatus(
  workorders: Workorder[] | undefined,
): RoutingKeyStatus {
  const isAdmin = useCanEditDayPlanning();

  return useMemo(() => {
    if (!workorders || workorders.length === 0) {
      return {
        currentKey: null,
        storedKey: null,
        sortOrderSource: null,
        hasChanges: false,
        status: 'unsorted' as RouteStatus,
        canAutoSort: false,
        canManualEdit: isAdmin,
        routingTimestamp: null,
      };
    }

    // Calculate current routing key from workorder data
    const currentKey = calculateRoutingKey(
      workorders.map((wo) => ({
        recnum: wo.recnum,
        location_address: wo.location_address,
        route_option: wo.route_option,
      })),
    );

    // Get stored values from first workorder (all should match)
    const storedKey = workorders[0]?.routing_key ?? null;
    const sortOrderSource = workorders[0]?.sort_order_source ?? null;
    const routingTimestamp = workorders[0]?.routing_timestamp ?? null;

    // Determine route status
    const status = getRouteStatus(sortOrderSource, storedKey, currentKey);
    const hasChanges = currentKey !== storedKey;

    return {
      currentKey,
      storedKey,
      sortOrderSource,
      hasChanges,
      status,
      canAutoSort: canAutoSortUtil(status, isAdmin),
      canManualEdit: canManualEditUtil(isAdmin),
      routingTimestamp,
    };
  }, [workorders, isAdmin]);
}

/**
 * Mutation to optimize (auto-sort) a route
 */
export function useOptimizeRouteMutation(
  date: string,
  userId: number | undefined,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('No user ID provided');
      return optimizeRoute(date, userId);
    },
    onMutate: async () => {
      if (!userId) return;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: dayPlanningQueries.day(userId, date).queryKey,
      });
    },
    onSettled: () => {
      // Always refetch after mutation settles
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: dayPlanningQueries.day(userId, date).queryKey,
        });
      }
      // Also invalidate all-technicians query
      queryClient.invalidateQueries({
        queryKey: dayPlanningQueries.allTechnicians(date).queryKey,
      });
      // Invalidate routes
      queryClient.invalidateQueries({
        queryKey: [...dayPlanningQueries.all(), 'routes'],
      });
    },
  });
}

/**
 * Mutation to manually update route order
 * Admin only - updates sort_order, planned_arrive_time, and route_option for all workorders
 */
export function useManualRouteUpdateMutation(
  date: string,
  userId: number | undefined,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      routeInfo: Array<{
        recnum: number;
        time: string | null;
        order: number;
        route_option: 'direct' | 'via_hq';
      }>,
    ) => {
      if (!userId) throw new Error('No user ID provided');
      return updateRouteManual(date, userId, routeInfo);
    },
    onMutate: async (routeInfo) => {
      if (!userId) return;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: dayPlanningQueries.day(userId, date).queryKey,
      });

      // Snapshot previous value
      const previousDay = queryClient.getQueryData(
        dayPlanningQueries.day(userId, date).queryKey,
      );

      // Optimistically update with new route info
      queryClient.setQueryData(
        dayPlanningQueries.day(userId, date).queryKey,
        (old: Workorder[] | undefined) => {
          if (!old) return old;

          // Create a map of recnum to route info for quick lookup
          const routeMap = new Map(routeInfo.map((r) => [r.recnum, r]));

          // Update workorders with new route info
          return old
            .map((wo) => {
              const route = routeMap.get(wo.recnum);
              if (!route) return wo;

              return {
                ...wo,
                sort_order: route.order,
                planned_arrive_time: route.time,
                route_option: route.route_option,
              };
            })
            .sort((a, b) => {
              // Sort by the new sort_order
              if (a.sort_order !== null && b.sort_order !== null) {
                return a.sort_order - b.sort_order;
              }
              return 0;
            });
        },
      );

      return { previousDay };
    },
    onError: (err, routeInfo, context) => {
      // Rollback on error
      if (context?.previousDay && userId) {
        queryClient.setQueryData(
          dayPlanningQueries.day(userId, date).queryKey,
          context.previousDay,
        );
      }
      toast.error('Manual route update failed', {
        description: 'Reverted to previous order',
      });
    },
    onSettled: () => {
      // Always refetch after mutation settles
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: dayPlanningQueries.day(userId, date).queryKey,
        });
      }
      // Also invalidate all-technicians query
      queryClient.invalidateQueries({
        queryKey: dayPlanningQueries.allTechnicians(date).queryKey,
      });
      // Invalidate routes
      queryClient.invalidateQueries({
        queryKey: [...dayPlanningQueries.all(), 'routes'],
      });
    },
  });
}
